<script setup>
// Seção "Fontes de dados do cliente" da tela Integrações (Fase 6, spec §4.6): as fontes PostgreSQL read-only da
// conta (nome, host, views, status sob demanda) com "Conectar PostgreSQL" em modal e as queries nomeadas de cada
// fonte (nome, parâmetros, quando roda). Faixa do desenho: nada aqui é obrigatório — o lead sempre fica salvo.
import { computed, onMounted, reactive, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  Add16,
  Checkmark16,
  Error16,
  Edit16,
  TrashCan16,
} from '@carbon/icons-vue';

import Button from 'dashboard/components-next/button/Button.vue';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import {
  Alert,
  AlertDescription,
} from 'dashboard/components-next/polaris/ui/alert';
import ConnectDataSourceDialog from './ConnectDataSourceDialog.vue';
import DataQueryDialog from './DataQueryDialog.vue';
import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';
import PolarisDataSourceAPI from 'dashboard/api/polaris/dataSources';

const { t } = useI18n();
const store = useStore();
const { checkPermissions } = usePolicy();

const i18nKey = 'POLARIS.DATA_SOURCES';

const sources = useMapGetter('polarisDataSources/getRecords');
const isFetching = computed(
  () => store.getters['polarisDataSources/getUIFlags'].fetchingList
);
const canManage = computed(() => checkPermissions(['administrator']));

const connectDialog = ref(null);
const showConnectDialog = ref(false);
const editingSource = ref(null);

const queryDialog = ref(null);
const showQueryDialog = ref(false);
const queryDialogSourceId = ref(null);
const editingQuery = ref(null);

const deleteDialog = ref(null);
const sourceToDelete = ref(null);

// Queries por fonte (carregadas sob demanda, atualizadas depois de cada mutação) e o resultado do último teste.
const queriesBySource = reactive({});
const testStatusBySource = reactive({});

const fetchSources = () => store.dispatch('polarisDataSources/get');

const fetchQueries = async sourceId => {
  const { data } = await PolarisDataSourceAPI.getQueries(sourceId);
  queriesBySource[sourceId] = data.payload || [];
};

const queriesOf = sourceId => queriesBySource[sourceId] || [];

const runOnLabel = runOn =>
  runOn === 'conversation_start'
    ? t(`${i18nKey}.QUERIES.RUN_ON_START`)
    : t(`${i18nKey}.QUERIES.RUN_ON_DEMAND`);

const paramsLabel = query =>
  (query.params || [])
    .map(param => (param.bind ? `${param.name} ⟵ ${param.bind}` : param.name))
    .join(', ');

const openConnectDialog = source => {
  editingSource.value = source;
  showConnectDialog.value = true;
  nextTick(() => connectDialog.value?.dialogRef?.open());
};

const openQueryDialog = (sourceId, query) => {
  queryDialogSourceId.value = sourceId;
  editingQuery.value = query;
  showQueryDialog.value = true;
  nextTick(() => queryDialog.value?.dialogRef?.open());
};

const confirmDeleteSource = source => {
  sourceToDelete.value = source;
  nextTick(() => deleteDialog.value?.dialogRef?.open());
};

const testSource = async source => {
  testStatusBySource[source.id] = { loading: true };
  try {
    const { data } = await PolarisDataSourceAPI.testSavedConnection(source.id);
    testStatusBySource[source.id] = { loading: false, ok: data.ok };
    if (data.ok) {
      useAlert(t(`${i18nKey}.TEST.SUCCESS_SHORT`));
    } else {
      useAlert(t(`${i18nKey}.TEST.FAILED`));
    }
  } catch (error) {
    testStatusBySource[source.id] = { loading: false, ok: false };
    useAlert(t(`${i18nKey}.TEST.FAILED`));
  }
};

const deleteQuery = async (sourceId, query) => {
  try {
    await PolarisDataSourceAPI.deleteQuery(sourceId, query.id);
    useAlert(t(`${i18nKey}.QUERIES.DELETE_SUCCESS`));
    fetchQueries(sourceId);
  } catch (error) {
    useAlert(t(`${i18nKey}.QUERIES.DELETE_ERROR`));
  }
};

const onSourceSaved = () => fetchSources();
const onQuerySaved = () => fetchQueries(queryDialogSourceId.value);
const onDeleteSuccess = () => {
  sourceToDelete.value = null;
  fetchSources();
};

const sourceMenuItems = () => [
  { label: t(`${i18nKey}.ACTIONS.EDIT`), value: 'edit', icon: Edit16 },
  {
    label: t(`${i18nKey}.ACTIONS.DELETE`),
    value: 'delete',
    icon: TrashCan16,
    destructive: true,
  },
];

const onSourceAction = (source, action) => {
  if (action === 'edit') openConnectDialog(source);
  if (action === 'delete') confirmDeleteSource(source);
};

onMounted(() => {
  if (!canManage.value) return; // a API de fontes é admin-only; nada a buscar para agentes
  fetchSources().then(() => {
    sources.value.forEach(source => fetchQueries(source.id));
  });
  store.dispatch('polarisAssistants/get', { page: 1 });
});
</script>

<template>
  <section
    v-if="canManage"
    class="flex flex-col gap-3"
    data-testid="data-sources-section"
  >
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ $t(`${i18nKey}.HEADER`) }}
        </h2>
        <p class="text-xs text-n-slate-11">
          {{ $t(`${i18nKey}.SUBHEADER`) }}
        </p>
      </div>
      <Button v-if="canManage" size="small" @click="openConnectDialog(null)">
        <Add16 />
        {{ $t(`${i18nKey}.CONNECT_BUTTON`) }}
      </Button>
    </div>

    <Alert role="note">
      <AlertDescription class="text-xs text-n-slate-11">
        {{ $t(`${i18nKey}.NOT_REQUIRED_NOTE`) }}
      </AlertDescription>
    </Alert>

    <p v-if="!isFetching && !sources.length" class="text-sm text-n-slate-11">
      {{ $t(`${i18nKey}.EMPTY`) }}
    </p>

    <div
      v-for="source in sources"
      :key="source.id"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-3"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex min-w-0 items-center gap-2">
          <span class="truncate text-sm font-medium text-n-slate-12">
            {{ source.name }}
          </span>
          <span class="truncate text-xs text-n-slate-11">
            {{ source.host }}:{{ source.port }}/{{ source.database }}
          </span>
          <Badge variant="secondary">{{ source.kind }}</Badge>
          <Badge v-if="source.mask_pii" variant="info">
            {{ $t(`${i18nKey}.MASK_ON`) }}
          </Badge>
          <button
            v-if="canManage"
            type="button"
            class="flex items-center gap-1 text-xs text-n-slate-11 hover:text-n-slate-12"
            :disabled="testStatusBySource[source.id]?.loading"
            @click="testSource(source)"
          >
            <Checkmark16 v-if="testStatusBySource[source.id]?.ok === true" />
            <Error16 v-else-if="testStatusBySource[source.id]?.ok === false" />
            {{ $t(`${i18nKey}.TEST.BUTTON_SHORT`) }}
          </button>
        </div>
        <RowMenu
          v-if="canManage"
          :label="`${$t(`${i18nKey}.ACTIONS.LABEL`)}: ${source.name}`"
          :items="sourceMenuItems()"
          @action="value => onSourceAction(source, value)"
        />
      </div>

      <div class="flex flex-wrap gap-1">
        <Badge
          v-for="view in source.allowed_views"
          :key="view"
          variant="outline"
        >
          {{ view }}
        </Badge>
      </div>

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between">
          <span class="text-xs font-medium text-n-slate-12">
            {{ $t(`${i18nKey}.QUERIES.HEADER`) }}
          </span>
          <button
            v-if="canManage"
            type="button"
            class="flex items-center gap-1 text-xs text-n-brand hover:underline"
            @click="openQueryDialog(source.id, null)"
          >
            <Add16 />
            {{ $t(`${i18nKey}.QUERIES.ADD`) }}
          </button>
        </div>
        <p v-if="!queriesOf(source.id).length" class="text-xs text-n-slate-11">
          {{ $t(`${i18nKey}.QUERIES.EMPTY`) }}
        </p>
        <div
          v-for="query in queriesOf(source.id)"
          :key="query.id"
          class="flex items-center justify-between gap-2 rounded-md bg-n-alpha-black2 px-2 py-1.5"
        >
          <div class="min-w-0">
            <span class="text-xs font-medium text-n-slate-12">
              {{ query.name }}
            </span>
            <span class="ml-2 font-mono text-xs text-n-slate-11">
              {{ query.key }}
            </span>
            <p class="truncate text-xs text-n-slate-11">
              {{ paramsLabel(query) || $t(`${i18nKey}.QUERIES.NO_PARAMS`) }}
              · {{ runOnLabel(query.run_on) }}
            </p>
          </div>
          <div v-if="canManage" class="flex shrink-0 gap-1">
            <button
              type="button"
              class="text-n-slate-11 hover:text-n-slate-12"
              @click="openQueryDialog(source.id, query)"
            >
              <Edit16 />
            </button>
            <button
              type="button"
              class="text-n-slate-11 hover:text-n-ruby-11"
              @click="deleteQuery(source.id, query)"
            >
              <TrashCan16 />
            </button>
          </div>
        </div>
      </div>
    </div>

    <ConnectDataSourceDialog
      v-if="showConnectDialog"
      ref="connectDialog"
      :source="editingSource"
      @saved="onSourceSaved"
      @close="showConnectDialog = false"
    />
    <DataQueryDialog
      v-if="showQueryDialog"
      ref="queryDialog"
      :source-id="queryDialogSourceId"
      :query="editingQuery"
      @saved="onQuerySaved"
      @close="showQueryDialog = false"
    />
    <DeleteDialog
      v-if="sourceToDelete"
      ref="deleteDialog"
      :entity="sourceToDelete"
      type="DataSources"
      translation-key="DATA_SOURCES"
      @delete-success="onDeleteSuccess"
    />
  </section>
</template>
